import { Card, RandomInt, newDeck, secureRandomInt, shuffle } from "./cards.ts";
import { evaluate, handName } from "./evaluator.ts";
import type { Cents } from "./money.ts";
import { formatUSD } from "./money.ts";
import { Pot, buildPots } from "./pots.ts";

export interface TableConfig {
  smallBlind: Cents;
  bigBlind: Cents;
  minBuyIn: Cents;
  maxBuyIn: Cents;
  /** Smallest chip; odd chips when splitting a pot are awarded in units of this. */
  unit: Cents;
  maxSeats: number;
}

export const DEFAULT_CONFIG: TableConfig = {
  smallBlind: 50,
  bigBlind: 100,
  minBuyIn: 4000, // 40 big blinds
  maxBuyIn: 10000, // 100 big blinds
  unit: 25,
  maxSeats: 6,
};

export type Street = "waiting" | "preflop" | "flop" | "turn" | "river" | "showdown";

export type Action =
  | { type: "fold" }
  | { type: "check" }
  | { type: "call" }
  /** `amount` is the TOTAL bet for this street ("raise to"), in cents. Also used for opening bets. */
  | { type: "raise"; amount: Cents }
  | { type: "allin" };

export interface Player {
  id: string;
  name: string;
  seat: number;
  stack: Cents;
  hole: Card[];
  /** Chips put in on the current street. */
  bet: Cents;
  /** Chips put in this whole hand. */
  total: Cents;
  folded: boolean;
  allIn: boolean;
  needsToAct: boolean;
  canRaise: boolean;
  /** Joined mid-hand or busted: skipped until the next hand. */
  sittingOut: boolean;
  /** Stood up mid-hand: folded now, removed when the hand ends. */
  leaving: boolean;
}

export type GameEvent =
  | { type: "player_joined"; playerId: string; seat: number; stack: Cents }
  | { type: "player_left"; playerId: string; seat: number }
  | { type: "hand_started"; handNumber: number; button: number }
  | { type: "blind"; playerId: string; kind: "small" | "big"; amount: Cents }
  | { type: "deal_hole" }
  | { type: "deal_board"; street: "flop" | "turn" | "river"; cards: Card[] }
  | { type: "action"; playerId: string; action: "fold" | "check" | "call" | "bet" | "raise" | "allin"; amount: Cents }
  | { type: "uncalled_bet_returned"; playerId: string; amount: Cents }
  | { type: "showdown"; reveals: Array<{ playerId: string; hole: Card[]; hand: string }> }
  | { type: "pot_awarded"; playerId: string; amount: Cents; potIndex: number; hand?: string }
  /** The host changed the table settings (blinds can only change before the game starts). */
  | { type: "settings_changed"; buyIn: Cents; smallBlind: Cents; bigBlind: Cents; buyInChanged: boolean; blindsChanged: boolean }
  /** The host started the game: the blinds are now locked and hands deal automatically. */
  | { type: "game_started"; smallBlind: Cents; bigBlind: Cents; buyIn: Cents }
  /** Every human left, so the table went back to a fresh lobby. */
  | { type: "game_over" }
  | { type: "hand_complete" };

export interface LegalActions {
  fold: boolean;
  check: boolean;
  call: Cents | null;
  /** Min and max "raise to" totals for this street; null if raising isn't allowed. */
  raise: { min: Cents; max: Cents } | null;
}

export class PokerError extends Error {}

export class PokerTable {
  readonly config: TableConfig;
  players: Array<Player | null>;
  button = -1;
  street: Street = "waiting";
  board: Card[] = [];
  handNumber = 0;
  /** Seat whose turn it is, or null when no one is to act. */
  toAct: number | null = null;
  currentBet: Cents = 0;
  minRaise: Cents = 0;
  pots: Pot[] = [];
  /** Events produced since the last drain; the dealer UI/announcer consumes these. */
  events: GameEvent[] = [];

  private deck: Card[] = [];
  private deckPos = 0;
  private rand: RandomInt;
  private showdownReveal = false;

  constructor(config: Partial<TableConfig> = {}, rand: RandomInt = secureRandomInt) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.rand = rand;
    this.players = new Array<Player | null>(this.config.maxSeats).fill(null);
  }

  // ---------------------------------------------------------------- seating

  sitDown(id: string, name: string, buyIn: Cents, seat?: number): Player {
    if (this.findPlayer(id)) throw new PokerError("Already seated");
    if (buyIn < this.config.minBuyIn || buyIn > this.config.maxBuyIn) {
      throw new PokerError(
        this.config.minBuyIn === this.config.maxBuyIn
          ? `The buy-in at this table is ${formatUSD(this.config.minBuyIn)}`
          : `Buy-in must be between ${formatUSD(this.config.minBuyIn)} and ${formatUSD(this.config.maxBuyIn)}`,
      );
    }
    if (buyIn % this.config.unit !== 0) throw new PokerError(`Buy-in must be a multiple of ${formatUSD(this.config.unit)}`);
    const s = seat ?? this.players.findIndex((p) => p === null);
    if (s < 0 || s >= this.config.maxSeats) throw new PokerError("Table is full");
    if (this.players[s]) throw new PokerError("Seat taken");
    const p: Player = {
      id, name, seat: s, stack: buyIn, hole: [], bet: 0, total: 0,
      folded: false, allIn: false, needsToAct: false, canRaise: false,
      sittingOut: this.street !== "waiting", leaving: false,
    };
    this.players[s] = p;
    this.emit({ type: "player_joined", playerId: id, seat: s, stack: buyIn });
    return p;
  }

  /** Leaving mid-hand folds you first; chips you already put in the pot stay there. Returns the chips you cash out. */
  standUp(id: string): Cents {
    const p = this.mustFind(id);
    const remove = (): Cents => {
      this.players[p.seat] = null;
      this.emit({ type: "player_left", playerId: id, seat: p.seat });
      return p.stack;
    };
    if (this.street === "waiting") return remove();
    if (p.sittingOut && p.total === 0) return remove(); // wasn't dealt into this hand

    const cashOut = p.stack;
    p.stack = 0;
    p.leaving = true;
    if (!p.folded) {
      if (this.toAct === p.seat) this.act(id, { type: "fold" });
      else this.forceFold(p);
    }
    this.emit({ type: "player_left", playerId: id, seat: p.seat });
    return cashOut;
  }

  /** Add chips between hands (rebuy / top-up). */
  rebuy(id: string, amount: Cents): void {
    const p = this.mustFind(id);
    if (this.street !== "waiting") throw new PokerError("Rebuy between hands only");
    if (amount % this.config.unit !== 0) throw new PokerError("Bad rebuy amount");
    if (p.stack + amount > this.config.maxBuyIn) throw new PokerError("Over the max buy-in");
    p.stack += amount;
    if (p.stack > 0) p.sittingOut = false;
  }

  findPlayer(id: string): Player | undefined {
    return this.players.find((p): p is Player => !!p && p.id === id);
  }

  private mustFind(id: string): Player {
    const p = this.findPlayer(id);
    if (!p) throw new PokerError("Not seated at this table");
    return p;
  }

  // ------------------------------------------------------------- hand flow

  canStartHand(): boolean {
    return this.street === "waiting" && this.eligibleSeats().length >= 2;
  }

  /** Pass a pre-ordered deck in tests. The deck is dealt from index 0. */
  startHand(testDeck?: Card[]): void {
    if (this.street !== "waiting") throw new PokerError("Hand already in progress");
    const seats = this.eligibleSeats();
    if (seats.length < 2) throw new PokerError("Need at least 2 players with chips");

    this.handNumber++;
    this.board = [];
    this.pots = [];
    this.currentBet = 0;
    this.minRaise = this.config.bigBlind;
    this.deck = testDeck ? [...testDeck] : shuffle(newDeck(), this.rand);
    this.deckPos = 0;
    this.showdownReveal = false;
    for (const p of this.players) {
      if (!p) continue;
      p.hole = []; p.bet = 0; p.total = 0; p.folded = false; p.allIn = false;
      p.needsToAct = false; p.canRaise = false;
    }

    this.button = this.nextSeat(this.button, seats);
    this.emit({ type: "hand_started", handNumber: this.handNumber, button: this.button });

    // Heads-up: the button is the small blind and acts first preflop.
    const headsUp = seats.length === 2;
    const sbSeat = headsUp ? this.button : this.nextSeat(this.button, seats);
    const bbSeat = this.nextSeat(sbSeat, seats);
    this.postBlind(this.players[sbSeat]!, this.config.smallBlind, "small");
    this.postBlind(this.players[bbSeat]!, this.config.bigBlind, "big");
    this.currentBet = this.config.bigBlind;

    // Deal two hole cards, one at a time, starting left of the button
    const order = this.orderFrom(this.nextSeat(this.button, seats), seats);
    for (let r = 0; r < 2; r++) for (const s of order) this.players[s]!.hole.push(this.draw());
    this.emit({ type: "deal_hole" });

    this.street = "preflop";
    for (const s of seats) {
      const p = this.players[s]!;
      p.canRaise = true;
      p.needsToAct = !p.allIn;
    }
    this.toAct = this.nextActor(bbSeat);
    this.advanceIfRoundOver();
  }

  private postBlind(p: Player, amount: Cents, kind: "small" | "big"): void {
    const paid = Math.min(amount, p.stack);
    this.commit(p, paid);
    this.emit({ type: "blind", playerId: p.id, kind, amount: paid });
  }

  // -------------------------------------------------------------- actions

  legalActions(id: string): LegalActions | null {
    const p = this.findPlayer(id);
    if (!p || this.toAct !== p.seat) return null;
    const toCall = this.currentBet - p.bet;
    const maxTo = p.bet + p.stack;
    let raise: LegalActions["raise"] = null;
    if (p.canRaise && maxTo > this.currentBet) {
      const minTo = Math.min(this.currentBet + this.minRaise, maxTo);
      raise = { min: minTo, max: maxTo };
    }
    return {
      fold: true,
      check: toCall === 0,
      call: toCall > 0 ? Math.min(toCall, p.stack) : null,
      raise,
    };
  }

  act(id: string, action: Action): void {
    const p = this.mustFind(id);
    if (this.toAct !== p.seat) throw new PokerError("It's not your turn");
    const toCall = this.currentBet - p.bet;

    switch (action.type) {
      case "fold":
        p.folded = true;
        p.needsToAct = false;
        this.emit({ type: "action", playerId: id, action: "fold", amount: 0 });
        break;

      case "check":
        if (toCall > 0) throw new PokerError("You can't check, there's a bet to call");
        p.needsToAct = false;
        p.canRaise = false;
        this.emit({ type: "action", playerId: id, action: "check", amount: 0 });
        break;

      case "call": {
        if (toCall <= 0) throw new PokerError("Nothing to call");
        const paid = Math.min(toCall, p.stack);
        this.commit(p, paid);
        p.needsToAct = false;
        p.canRaise = false;
        this.emit({ type: "action", playerId: id, action: p.allIn ? "allin" : "call", amount: paid });
        break;
      }

      case "allin":
        if (p.stack <= toCall) return this.act(id, { type: "call" });
        return this.raiseTo(p, p.bet + p.stack);

      case "raise":
        return this.raiseTo(p, action.amount);
    }
    this.afterAction(p.seat);
  }

  private raiseTo(p: Player, to: Cents): void {
    if (!Number.isInteger(to) || to % this.config.unit !== 0 && to !== p.bet + p.stack) {
      throw new PokerError(`Bets must be in multiples of ${formatUSD(this.config.unit)}`);
    }
    const maxTo = p.bet + p.stack;
    if (to > maxTo) throw new PokerError("You don't have enough chips");
    if (to <= this.currentBet) throw new PokerError("A raise must be larger than the current bet");
    if (!p.canRaise) throw new PokerError("Betting wasn't re-opened, you can only call or fold");

    const increment = to - this.currentBet;
    const isAllIn = to === maxTo;
    if (increment < this.minRaise && !isAllIn) {
      throw new PokerError(`Minimum raise is to ${formatUSD(this.currentBet + this.minRaise)}`);
    }
    const wasBet = this.currentBet === 0;
    this.commit(p, to - p.bet);
    const fullRaise = increment >= this.minRaise;
    this.currentBet = to;
    p.needsToAct = false;
    p.canRaise = false;
    for (const o of this.players) {
      if (!o || o === p || o.folded || o.allIn || o.sittingOut) continue;
      o.needsToAct = true;
      if (fullRaise) o.canRaise = true; // a short all-in does NOT re-open betting
    }
    if (fullRaise) this.minRaise = increment;
    this.emit({
      type: "action", playerId: p.id,
      action: p.allIn ? "allin" : wasBet ? "bet" : "raise",
      amount: to,
    });
    this.afterAction(p.seat);
  }

  private afterAction(fromSeat: number): void {
    const live = this.contenders();
    if (live.length === 1) return this.finishUncontested(live[0]!);
    this.toAct = this.nextActor(fromSeat);
    this.advanceIfRoundOver();
  }

  /** Move to the next street (or showdown) once nobody needs to act. */
  private advanceIfRoundOver(): void {
    while (this.toAct === null && this.street !== "waiting" && this.street !== "showdown") {
      const live = this.contenders();
      if (live.length === 1) return this.finishUncontested(live[0]!);

      this.collectBets();
      if (this.street === "river") return this.showdown();

      const next = { preflop: "flop", flop: "turn", turn: "river" }[this.street as "preflop" | "flop" | "turn"] as
        "flop" | "turn" | "river";
      this.street = next;
      this.draw(); // burn
      const cards = next === "flop" ? [this.draw(), this.draw(), this.draw()] : [this.draw()];
      this.board.push(...cards);
      this.emit({ type: "deal_board", street: next, cards });

      this.currentBet = 0;
      this.minRaise = this.config.bigBlind;
      for (const p of this.players) {
        if (!p || p.folded || p.sittingOut) continue;
        p.canRaise = true;
        p.needsToAct = !p.allIn;
      }
      // Postflop: first live, non-all-in player left of the button acts first.
      this.toAct = this.nextActor(this.button);
      // If at most one player can still bet, there's no betting: loop and run out the board.
      if (this.players.filter((p) => p && !p.folded && !p.allIn && !p.sittingOut).length <= 1) {
        for (const p of this.players) if (p) p.needsToAct = false;
        this.toAct = null;
      }
    }
  }

  private collectBets(): void {
    for (const p of this.players) if (p) p.bet = 0;
    this.currentBet = 0;
  }

  // --------------------------------------------------------- hand endings

  private finishUncontested(winner: Player): void {
    this.collectBets();
    const pot = this.players.reduce((s, p) => s + (p ? p.total : 0), 0);
    winner.stack += pot;
    this.emit({ type: "pot_awarded", playerId: winner.id, amount: pot, potIndex: 0 });
    this.endHand();
  }

  private showdown(): void {
    this.street = "showdown";
    this.showdownReveal = true;
    this.toAct = null;
    const live = this.contenders();
    const scores = new Map<number, number>();
    for (const p of live) scores.set(p.seat, evaluate([...p.hole, ...this.board]));
    this.emit({
      type: "showdown",
      reveals: live.map((p) => ({ playerId: p.id, hole: p.hole, hand: handName(scores.get(p.seat)!) })),
    });

    const contribs = this.players
      .filter((p): p is Player => !!p && p.total > 0)
      .map((p) => ({ seat: p.seat, amount: p.total, folded: p.folded }));
    this.pots = buildPots(contribs);

    this.pots.forEach((pot, idx) => {
      if (pot.eligible.length === 1) {
        // Uncalled bet: goes straight back
        const p = this.players[pot.eligible[0]!]!;
        p.stack += pot.amount;
        this.emit({ type: "uncalled_bet_returned", playerId: p.id, amount: pot.amount });
        return;
      }
      const best = Math.max(...pot.eligible.map((s) => scores.get(s)!));
      const winners = pot.eligible.filter((s) => scores.get(s) === best);
      const share = Math.floor(pot.amount / this.config.unit / winners.length) * this.config.unit;
      let odd = pot.amount - share * winners.length;
      // Odd chips go to the winners closest to the left of the button
      const ordered = this.orderFrom(this.nextSeat(this.button, this.occupiedSeats()), this.occupiedSeats()).filter((s) =>
        winners.includes(s));
      for (const s of ordered) {
        let win = share;
        if (odd > 0) { const extra = Math.min(odd, this.config.unit); win += extra; odd -= extra; }
        const p = this.players[s]!;
        p.stack += win;
        this.emit({ type: "pot_awarded", playerId: p.id, amount: win, potIndex: idx, hand: handName(best) });
      }
    });
    this.endHand();
  }

  private endHand(): void {
    this.toAct = null;
    this.emit({ type: "hand_complete" });
    this.street = "waiting";
    // Hole cards stay readable on the table until the next hand starts (for the showdown view).
    for (let i = 0; i < this.players.length; i++) {
      const p = this.players[i];
      if (!p) continue;
      p.bet = 0;
      p.needsToAct = false;
      if (p.leaving) this.players[i] = null;
      else if (p.stack === 0) p.sittingOut = true; // busted: must rebuy
      else p.sittingOut = false; // mid-hand joiners get dealt in next hand
    }
  }

  // ------------------------------------------------------------- helpers

  private commit(p: Player, amount: Cents): void {
    p.stack -= amount;
    p.bet += amount;
    p.total += amount;
    if (p.stack === 0) p.allIn = true;
  }

  private forceFold(p: Player): void {
    p.folded = true;
    p.needsToAct = false;
    this.emit({ type: "action", playerId: p.id, action: "fold", amount: 0 });
    const live = this.contenders();
    if (live.length === 1) this.finishUncontested(live[0]!);
  }

  private draw(): Card {
    const c = this.deck[this.deckPos++];
    if (c === undefined) throw new PokerError("Deck exhausted");
    return c;
  }

  /** Players still contesting the pot. */
  private contenders(): Player[] {
    return this.players.filter((p): p is Player => !!p && !p.folded && !p.sittingOut && p.hole.length === 2);
  }

  private occupiedSeats(): number[] {
    return this.players.flatMap((p, i) => (p ? [i] : []));
  }

  /** Seats dealt into the next hand. */
  private eligibleSeats(): number[] {
    return this.players.flatMap((p, i) => (p && !p.sittingOut && p.stack > 0 ? [i] : []));
  }

  /** Next seat clockwise after `from` that is in `seats`. */
  private nextSeat(from: number, seats: number[]): number {
    const n = this.config.maxSeats;
    for (let i = 1; i <= n; i++) {
      const s = (from + i + n) % n;
      if (seats.includes(s)) return s;
    }
    throw new PokerError("No seats");
  }

  /** All of `seats` in clockwise order starting at `start` (inclusive). */
  private orderFrom(start: number, seats: number[]): number[] {
    const n = this.config.maxSeats;
    const out: number[] = [];
    for (let i = 0; i < n; i++) {
      const s = (start + i) % n;
      if (seats.includes(s)) out.push(s);
    }
    return out;
  }

  /** Next seat after `from` that is live, not all-in, and still needs to act. */
  private nextActor(from: number): number | null {
    const n = this.config.maxSeats;
    for (let i = 1; i <= n; i++) {
      const s = (from + i) % n;
      const p = this.players[s];
      if (p && !p.folded && !p.allIn && !p.sittingOut && p.needsToAct) return s;
    }
    return null;
  }

  private emit(e: GameEvent): void {
    this.events.push(e);
  }

  drainEvents(): GameEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  // ------------------------------------------------------------ client views

  /** What a given player (or a spectator, with null) is allowed to see. Never leaks other players' hole cards. */
  viewFor(viewerId: string | null) {
    const revealAll = this.street === "waiting" && this.showdownReveal;
    return {
      street: this.street,
      handNumber: this.handNumber,
      button: this.button,
      toAct: this.toAct,
      board: this.board,
      currentBet: this.currentBet,
      minRaise: this.minRaise,
      pot: this.players.reduce((s, p) => s + (p ? p.total : 0), 0),
      players: this.players.map((p) =>
        p && {
          id: p.id, name: p.name, seat: p.seat, stack: p.stack, bet: p.bet,
          folded: p.folded, allIn: p.allIn, sittingOut: p.sittingOut,
          hole: p.id === viewerId || (revealAll && !p.folded && p.hole.length === 2) ? p.hole : p.hole.map(() => -1),
        }),
      legal: viewerId ? this.legalActions(viewerId) : null,
    };
  }
}
